import { tenantA4Letter as phmA4Letter } from '../../../assets/letter/tenants/phm/a4_letter';
import { tenantA4Letter2nd as phmA4Letter2nd } from '../../../assets/letter/tenants/phm/a4_letter_2nd_page';
import { tenantJobLabel as phmJobLabel } from '../../../assets/letter/tenants/phm/job_label';
import { tenantA4Letter as ndtA4Letter } from '../../../assets/letter/tenants/ndt/a4_letter';
import { tenantA4Letter2nd as ndtA4Letter2nd } from '../../../assets/letter/tenants/ndt/a4_letter_2nd_page';
import { tenantJobLabel as ndtJobLabel } from '../../../assets/letter/tenants/ndt/job_label';
import { tenantA4Letter as demoA4Letter } from '../../../assets/letter/tenants/demo/a4_letter';
import { tenantA4Letter2nd as demoA4Letter2nd } from '../../../assets/letter/tenants/demo/a4_letter_2nd_page';
import { tenantJobLabel as demoJobLabel } from '../../../assets/letter/tenants/demo/job_label';

export type CrmProvider = 'legacy' | 'hubspot';

export interface TenantBrandingAssets {
  appTitle: string;
  logoNavbar: string;
  logoLogin: string;
  loginBackground: string;
  fontFamily: string;
  loginBackgroundPosition: string;
  loginBackgroundSize: string;
}

export interface TenantLetterAssets {
  a4Letter: any;
  a4Letter2nd: any;
  jobLabel: any;
}

export interface TenantOrderDocumentation {
  /**
   * Satz, der den Umfang der Nachweispflicht beschreibt -- folgt im Bestell-PDF
   * direkt auf den Hinweis zum Doku-Postfach.
   */
  scopeNotice: string;
  /** Beispiele fuer beizubringende Nachweise, jeweils ohne Aufzaehlungszeichen. */
  certificateExamples: string[];
  /** Schlusssatz. Fehlt er, endet der Abschnitt nach der Aufzaehlung. */
  closingNotice?: string;
}

export interface TenantEInvoiceAddress {
  street: string;
  city: string;
  postalCode: string;
  /** ISO-3166-1-alpha-2, z.B. "DE". */
  countryCode: string;
}

export interface TenantEInvoiceBankAccount {
  /** IBAN ohne Leerzeichen. */
  iban: string;
  /** Kontoinhaber, wie ihn das Kreditinstitut fuehrt. */
  accountName: string;
  bic: string;
}

/**
 * Absenderstammdaten der X-Rechnung (cac:AccountingSupplierParty).
 * Der persoenliche Ansprechpartner kommt weiterhin aus dem Mitarbeiterstamm.
 */
export interface TenantEInvoiceParty {
  /** BT-34: elektronische Adresse des Rechnungsstellers, schemeID="EM". */
  endpointEmail: string;
  /**
   * BT-29: Kennung des Verkaeufers, die der Rechnungsempfaenger vergeben hat
   * (Kreditoren-/Lieferantennummer). Fehlt sie, entfaellt PartyIdentification.
   */
  partyIdentificationId?: string;
  address: TenantEInvoiceAddress;
  /** BT-31: USt-IdNr. des Rechnungsstellers. */
  vatId: string;
  /**
   * BT-27: eingetragener Name. Kann von legalCompanyName abweichen, weil hier
   * die Schreibweise aus dem Handelsregister gilt.
   */
  registrationName: string;
  /** BT-30: Registernummer, z.B. "HRB 35979". */
  registrationId?: string;
  /** BT-84/BT-85/BT-86: Zahlungsempfaenger. */
  bankAccount: TenantEInvoiceBankAccount;
}

export interface TenantConfig {
  id: string;
  displayName: string;
  crmProvider: CrmProvider;
  hostPatterns: string[];
  legalCompanyName: string;
  letterCity: string;
  /**
   * Postfach, das der Rechnungsbogen unter "Ihr Ansprechpartner" nennt --
   * statt der persoenlichen Adresse des Erstellers. Fehlt es, steht dort die
   * Adresse aus dem Mitarbeiterstamm.
   */
  invoiceContactEmail?: string;
  /**
   * Postfach, an das Lieferanten ihre Rechnungen zur Bestellung senden sollen.
   * Fehlt es, entfaellt der Hinweis auf die elektronische Rechnung im
   * Bestell-PDF.
   */
  supplierInvoiceEmail?: string;
  /**
   * Postfach fuer die vom Auftragnehmer beizubringenden Pruefbescheinigungen.
   * Fehlt es, nennt das Bestell-PDF keine Mailadresse fuer die Dokumentation.
   */
  certificateEmail?: string;
  /**
   * Abschnitt "Dokumentation" des Bestell-PDF. Fehlt er, entfaellt der
   * Abschnitt vollstaendig.
   */
  orderDocumentation?: TenantOrderDocumentation;
  /** Absenderstammdaten fuer die X-Rechnung (UBL). */
  eInvoiceParty: TenantEInvoiceParty;
  branding: TenantBrandingAssets;
  letterAssets: TenantLetterAssets;
}

const SHARED_PHM_LETTERS: TenantLetterAssets = {
  a4Letter: phmA4Letter,
  a4Letter2nd: phmA4Letter2nd,
  jobLabel: phmJobLabel,
};

const SHARED_NDT_LETTERS: TenantLetterAssets = {
  a4Letter: ndtA4Letter,
  a4Letter2nd: ndtA4Letter2nd,
  jobLabel: ndtJobLabel,
};

const SHARED_DEMO_LETTERS: TenantLetterAssets = {
  a4Letter: demoA4Letter,
  a4Letter2nd: demoA4Letter2nd,
  jobLabel: demoJobLabel,
};

/** Nachweise aus dem Anlagen- und Fertigungsgeschaeft. */
const FABRICATION_ORDER_DOCUMENTATION: TenantOrderDocumentation = {
  scopeNotice: 'Für jedes Bauteil muss eine Prüfbescheinigung erstellt werden.',
  certificateExamples: [
    'Ursprungsnachweis',
    'Maßprotokoll (Istwerte tolerierter Maße je Bauteil dokumentieren)',
    'Materialzeugnis - Wärmeprotokoll - Härteprotokoll',
    'Druckprüfung',
    'Schweißnahtprüfungen',
    'etc.',
  ],
  closingNotice: 'Bei Nichteinhaltung erfolgt keine Zahlungsfreigabe.',
};

/** Nachweise fuer zugekaufte Prueftechnik. */
const TEST_EQUIPMENT_ORDER_DOCUMENTATION: TenantOrderDocumentation = {
  scopeNotice: 'Für jeden Artikel muss eine Prüfbescheinigung erstellt werden.',
  certificateExamples: [
    'Ursprungsnachweis',
    'Konformitätserklärung (CE)',
    'Kalibrier-/Prüfzertifikat mit Rückführbarkeit',
    'Werks-/Abnahmeprüfzeugnis nach EN 10204',
    'Datenblatt bzw. Spezifikationsnachweis zum gelieferten Artikel',
    'etc.',
  ],
};

const PHM_EINVOICE_PARTY: TenantEInvoiceParty = {
  endpointEmail: 'rechnung@phm-burg.de',
  partyIdentificationId: '342064',
  address: {
    street: 'Kiefernweg 17',
    city: 'Burg',
    postalCode: '39288',
    countryCode: 'DE',
  },
  vatId: 'DE154347770',
  registrationName: 'PHM-Industrieanlagen GmbH',
  registrationId: 'HRB2398',
  bankAccount: {
    iban: 'DE63810632380001089722',
    accountName: 'PHM-Industrieanlagen GmbH',
    bic: 'GENODEF1BRG',
  },
};

const NDT_EINVOICE_PARTY: TenantEInvoiceParty = {
  endpointEmail: 'invoices@ndt-division.de',
  address: {
    street: 'Gutensweger Straße 11',
    city: 'Magdeburg',
    postalCode: '39126',
    countryCode: 'DE',
  },
  vatId: 'DE454777313',
  registrationName: 'NDT Division GmbH',
  registrationId: 'HRB 35979',
  bankAccount: {
    iban: 'DE03810400000253089700',
    accountName: 'NDT Division GmbH',
    bic: 'COBADEFFXXX',
  },
};

/** Platzhalterdaten des Demo-Mandanten, identisch zum Demo-Briefbogen. */
const DEMO_EINVOICE_PARTY: TenantEInvoiceParty = {
  endpointEmail: 'info@demo-gmbh.de',
  address: {
    street: 'Musterstraße 1',
    city: 'Berlin',
    postalCode: '10115',
    countryCode: 'DE',
  },
  vatId: 'DE123456789',
  registrationName: 'Demo GmbH',
  registrationId: 'HRB 123456',
  bankAccount: {
    iban: 'DE00123456789012345600',
    accountName: 'Demo GmbH',
    bic: 'MUSTDE00XXX',
  },
};

const SHARED_PHM_BRANDING: TenantBrandingAssets = {
  appTitle: 'PHM PPS',
  logoNavbar: '/assets/images/tenants/phm/logo-navbar.png',
  logoLogin: '/assets/images/tenants/phm/login-logo.png',
  loginBackground: '/assets/images/tenants/phm/login-bg.svg',
  fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
  loginBackgroundPosition: '25.2rem 0',
  loginBackgroundSize: '80%',
};

const TENANT_CONFIGS: Record<string, TenantConfig> = {
  phm: {
    id: 'phm',
    displayName: 'PHM PPS x',
    crmProvider: 'legacy',
    hostPatterns: [
      'localhost',
      '127.0.0.1',
      'dev-pps.wmedia.de',
      'uat-phm.portxx.de',
      'phm.portxx.de',
    ],
    legalCompanyName: 'PHM Industrieanlagen GmbH',
    letterCity: 'Burg',
    invoiceContactEmail: 'rechnung@phm-burg.de',
    supplierInvoiceEmail: 'rechnung@phm-burg.de',
    certificateEmail: 'doku@phm-burg.de',
    orderDocumentation: FABRICATION_ORDER_DOCUMENTATION,
    eInvoiceParty: PHM_EINVOICE_PARTY,
    branding: SHARED_PHM_BRANDING,
    letterAssets: SHARED_PHM_LETTERS,
  },
  demo: {
    id: 'demo',
    displayName: 'PPS Demo',
    crmProvider: 'legacy',
    hostPatterns: ['pps-demo.wmedia-di.de'],
    legalCompanyName: 'The PPD Demo GmbH',
    letterCity: 'Magdeburg',
    orderDocumentation: FABRICATION_ORDER_DOCUMENTATION,
    eInvoiceParty: DEMO_EINVOICE_PARTY,
    branding: {
      appTitle: 'PPS Demo',
      logoNavbar: '/assets/images/tenants/demo/logo-demo-navbar.png',
      logoLogin: '/assets/images/tenants/demo/logo-login.png',
      loginBackground: '/assets/images/tenants/demo/login-bg-2.svg',
      fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
      loginBackgroundPosition: '68& top',
      loginBackgroundSize: 'cover',
    },
    letterAssets: SHARED_DEMO_LETTERS,
  },
  ndt: {
    id: 'ndt',
    displayName: 'NDT PPS',
    crmProvider: 'hubspot',
    hostPatterns: ['pps.ndt-division.de', 'pps-uat.ndt-division.de'],
    legalCompanyName: 'NDT Division GmbH',
    letterCity: 'Magdeburg',
    supplierInvoiceEmail: 'invoices@ndt-division.de',
    certificateEmail: 'accounting@ndt-division.de',
    orderDocumentation: TEST_EQUIPMENT_ORDER_DOCUMENTATION,
    eInvoiceParty: NDT_EINVOICE_PARTY,
    branding: {
      appTitle: 'NDT PPS',
      logoNavbar: '/assets/images/tenants/ndt/logo-navbar.png',
      logoLogin: '/assets/images/tenants/ndt/login-logo.svg',
      loginBackground: '/assets/images/tenants/ndt/login-bg.svg',
      fontFamily: '"Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      loginBackgroundPosition: '68% top',
      loginBackgroundSize: 'cover',
    },
    letterAssets: SHARED_NDT_LETTERS,
  },
  'ndt-dev': {
    id: 'ndt-dev',
    displayName: 'NDT PPS (DEV)',
    crmProvider: 'hubspot',
    hostPatterns: ['pps-dev.ndt-division.de'],
    legalCompanyName: 'NDT Division GmbH',
    letterCity: 'Magdeburg',
    supplierInvoiceEmail: 'invoices@ndt-division.de',
    certificateEmail: 'accounting@ndt-division.de',
    orderDocumentation: TEST_EQUIPMENT_ORDER_DOCUMENTATION,
    eInvoiceParty: NDT_EINVOICE_PARTY,
    branding: {
      appTitle: 'NDT PPS (DEV)',
      logoNavbar: '/assets/images/tenants/ndt/logo-navbar.png',
      logoLogin: '/assets/images/tenants/ndt/login-logo.svg',
      loginBackground: '/assets/images/tenants/ndt/login-bg.svg',
      fontFamily: '"Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      loginBackgroundPosition: '68% top',
      loginBackgroundSize: 'cover',
    },
    letterAssets: SHARED_NDT_LETTERS,
  },
};

const TENANT_ALIASES: Record<string, string> = {
  default: 'phm',
  pps: 'phm',
  'pps-demo': 'demo',
  ndtdivision: 'ndt',
  'ndt-division': 'ndt',
};

export const DEFAULT_TENANT_ID = 'phm';

export function normalizeTenantId(candidate?: string | null): string | undefined {
  if (!candidate) {
    return undefined;
  }
  const normalized = candidate.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  return TENANT_ALIASES[normalized] ?? normalized;
}

export function getTenantConfigById(candidate?: string | null): TenantConfig | undefined {
  const normalized = normalizeTenantId(candidate);
  if (!normalized) {
    return undefined;
  }
  return TENANT_CONFIGS[normalized];
}

function hostMatchesPattern(hostname: string, pattern: string): boolean {
  const normalizedHost = hostname.toLowerCase();
  const normalizedPattern = pattern.toLowerCase();

  if (normalizedPattern.startsWith('*.')) {
    const suffix = normalizedPattern.slice(2);
    return normalizedHost === suffix || normalizedHost.endsWith(`.${suffix}`);
  }

  return normalizedHost === normalizedPattern;
}

export function getTenantConfigForHost(hostname?: string | null): TenantConfig | undefined {
  if (!hostname) {
    return undefined;
  }
  const normalizedHost = hostname.trim().toLowerCase();
  if (!normalizedHost) {
    return undefined;
  }

  for (const config of Object.values(TENANT_CONFIGS)) {
    if (config.hostPatterns.some(pattern => hostMatchesPattern(normalizedHost, pattern))) {
      return config;
    }
  }

  return undefined;
}

export function getDefaultTenantConfig(): TenantConfig {
  return TENANT_CONFIGS[DEFAULT_TENANT_ID];
}
